import axios, { CanceledError } from 'axios';
import { API_TIMEOUT_MS } from '@/api/constants';
import { getAccessToken } from '@/auth/accessToken';
import { getProfileIdentity, handleAuthError } from '@/auth/session';
import { API_BASE_URL } from '@/config/env';

const AUTH_ERROR_STATUSES = [401, 403];
const MAX_QUERY_RETRIES = 3;

const hasAuthErrorStatus = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  AUTH_ERROR_STATUSES.includes(error.response?.status ?? 0);

export const shouldRetryQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  const isAuthFailure = hasAuthErrorStatus(error) || axios.isCancel(error);

  if (isAuthFailure) {
    return false;
  }

  return failureCount < MAX_QUERY_RETRIES;
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(async (config) => {
  const token = await getAccessToken();

  if (!token) {
    handleAuthError();
    throw new CanceledError('Missing access token');
  }

  config.headers.Authorization = `Bearer ${token}`;

  const profileIdentity = getProfileIdentity();

  if (profileIdentity) {
    config.headers['x-profile-id'] = profileIdentity.profileId;
    config.headers['x-class-id'] = profileIdentity.classId;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (hasAuthErrorStatus(error)) {
      handleAuthError();
    }

    return Promise.reject(error);
  },
);
